import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  Loader2,
  Trash2,
  MoreHorizontal,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  createProject,
  deleteProject,
  fetchProjectsByStatus,
  archiveProject,
  unarchiveProject,
} from "@/api";

const TAG_COLORS = ["#8A9A86", "#C27A62", "#A87E6B", "#6B7B8A", "#B8A584", "#7A6B8A"];

export default function Projects() {
  const [activeProjects, setActiveProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [view, setView] = useState("active"); // "active" | "archived"
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [active, archived] = await Promise.all([
        fetchProjectsByStatus("false"),
        fetchProjectsByStatus("true"),
      ]);
      setActiveProjects(active);
      setArchivedProjects(archived);
    } catch (e) {
      toast.error("Could not load projects");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const color = TAG_COLORS[activeProjects.length % TAG_COLORS.length];
      const created = await createProject({ ...form, color_tag: color });
      setActiveProjects([created, ...activeProjects]);
      setForm({ name: "", client: "", description: "" });
      setOpenNew(false);
      toast.success("Project created");
    } catch (e) {
      toast.error("Failed to create project");
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmDelete() {
    const p = pendingDelete;
    if (!p) return;
    try {
      await deleteProject(p.id);
      setActiveProjects((prev) => prev.filter((x) => x.id !== p.id));
      setArchivedProjects((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Project deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setPendingDelete(null);
    }
  }

  async function handleArchive(p) {
    try {
      const updated = await archiveProject(p.id);
      setActiveProjects((prev) => prev.filter((x) => x.id !== p.id));
      setArchivedProjects((prev) => [updated, ...prev]);
      toast.success(`"${p.name}" archived`);
    } catch {
      toast.error("Failed to archive");
    }
  }

  async function handleUnarchive(p) {
    try {
      const updated = await unarchiveProject(p.id);
      setArchivedProjects((prev) => prev.filter((x) => x.id !== p.id));
      setActiveProjects((prev) => [updated, ...prev]);
      toast.success(`"${p.name}" restored`);
    } catch {
      toast.error("Failed to restore");
    }
  }

  const visible = view === "active" ? activeProjects : archivedProjects;

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-16" data-testid="projects-page">
      <div className="flex items-start justify-between mb-10 md:mb-14 gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">
            Studio
          </p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-stone-900">
            Your projects
          </h1>
          <p className="mt-3 text-stone-500 max-w-lg">
            A visual workspace for every space you're shaping — references,
            moodboards, materials, and floorplans.
          </p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-project-button"
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800 shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              New project
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-project-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">New project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
                  Name
                </label>
                <Input
                  data-testid="project-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Hampstead Townhouse"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
                  Client
                </label>
                <Input
                  data-testid="project-client-input"
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
                  Brief
                </label>
                <Textarea
                  data-testid="project-description-input"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="One line about the project..."
                  className="mt-1"
                />
              </div>
              <div className="pt-2 flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-project-button"
                  className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 mb-8" data-testid="projects-view-toggle">
        <button
          type="button"
          onClick={() => setView("active")}
          data-testid="projects-view-active"
          className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
            view === "active"
              ? "bg-stone-900 text-white"
              : "bg-stone-100 text-stone-700 hover:bg-stone-200"
          }`}
        >
          Active
          <span
            className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${
              view === "active" ? "bg-white/20 text-white" : "bg-stone-200/80 text-stone-600"
            }`}
          >
            {activeProjects.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setView("archived")}
          data-testid="projects-view-archived"
          className={`px-4 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1.5 ${
            view === "archived"
              ? "bg-stone-900 text-white"
              : "bg-stone-100 text-stone-700 hover:bg-stone-200"
          }`}
        >
          <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
          Archived
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              view === "archived" ? "bg-white/20 text-white" : "bg-stone-200/80 text-stone-600"
            }`}
          >
            {archivedProjects.length}
          </span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : visible.length === 0 ? (
        view === "active" ? (
          <EmptyState onCreate={() => setOpenNew(true)} />
        ) : (
          <EmptyArchived />
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visible.map((p, i) => (
            <ProjectTile
              key={p.id}
              project={p}
              index={i}
              onArchive={() => handleArchive(p)}
              onUnarchive={() => handleUnarchive(p)}
              onRequestDelete={() => setPendingDelete(p)}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Delete "{pendingDelete?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes all images, moodboards, floorplans, products
              and notes in this project. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-project"
              onClick={confirmDelete}
            >
              Delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProjectTile({ project, index, onArchive, onUnarchive, onRequestDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const archived = !!project.archived;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.5) }}
    >
      <Link
        to={`/project/${project.id}`}
        data-testid={`project-tile-${project.id}`}
        className={`group block relative aspect-[4/5] rounded-2xl overflow-hidden bg-stone-100 ${
          archived ? "opacity-80 hover:opacity-100 transition-opacity" : ""
        }`}
      >
        {project.cover_image ? (
          <img
            src={project.cover_image}
            alt={project.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${project.color_tag || "#8A9A86"}33 0%, #F7F7F5 100%)`,
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="w-16 h-16 rounded-full"
                style={{ background: project.color_tag || "#8A9A86", opacity: 0.4 }}
              />
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/10 to-transparent" />

        {archived && (
          <div
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-amber-100/95 text-amber-800 text-[10px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1"
            data-testid={`archived-badge-${project.id}`}
          >
            <Archive className="w-3 h-3" strokeWidth={1.6} />
            Archived
          </div>
        )}

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid={`project-menu-${project.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-stone-700 hover:text-stone-900 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity"
            >
              <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {archived ? (
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUnarchive();
                }}
                data-testid={`project-unarchive-${project.id}`}
              >
                <ArchiveRestore className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onArchive();
                }}
                data-testid={`project-archive-${project.id}`}
              >
                <Archive className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRequestDelete();
              }}
              data-testid={`delete-project-${project.id}`}
              className="text-red-600 focus:text-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span
            className="inline-block w-2 h-2 rounded-full mb-3"
            style={{ background: project.color_tag || "#8A9A86" }}
          />
          <h3 className="font-display text-2xl text-white tracking-tight">
            {project.name}
          </h3>
          {project.client && (
            <p className="text-white/75 text-sm mt-1">{project.client}</p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      className="border border-dashed border-stone-200 rounded-2xl py-24 text-center"
      data-testid="empty-projects"
    >
      <div className="w-16 h-16 rounded-full bg-stone-100 mx-auto flex items-center justify-center mb-6">
        <Plus className="w-7 h-7 text-stone-500" strokeWidth={1.5} />
      </div>
      <h3 className="font-display text-2xl text-stone-900">Start your first project</h3>
      <p className="text-stone-500 mt-2 max-w-md mx-auto">
        Create a project to collect inspiration, site photos, materials and plans
        — all in one visual canvas.
      </p>
      <Button
        onClick={onCreate}
        data-testid="empty-create-button"
        className="mt-6 rounded-full bg-stone-900 text-white hover:bg-stone-800"
      >
        Create a project
      </Button>
    </div>
  );
}

function EmptyArchived() {
  return (
    <div
      className="border border-dashed border-stone-200 rounded-2xl py-24 text-center"
      data-testid="empty-archived"
    >
      <div className="w-16 h-16 rounded-full bg-stone-100 mx-auto flex items-center justify-center mb-6">
        <Archive className="w-7 h-7 text-stone-500" strokeWidth={1.5} />
      </div>
      <h3 className="font-display text-2xl text-stone-900">Nothing archived yet</h3>
      <p className="text-stone-500 mt-2 max-w-md mx-auto">
        When a project wraps up, archive it from the project workspace or the
        card menu to keep your studio tidy. You can restore it any time.
      </p>
    </div>
  );
}
